import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  BellIcon,
  EyeIcon,
  EyeSlashIcon,
  UsersIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ProfileFormData {
  name: string;
  jobTitle: string;
  phone: string;
  timezone: string;
}

interface EmailChangeFormData {
  newEmail: string;
  confirmEmail: string;
}

interface CompanyFormData {
  companyName: string;
  companyWebsite: string;
  companyIndustry: string;
  companySize: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  additionalInfo: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

  interface NotificationPreferencesFormData {
    // Email preferences
    usage_alerts_email: boolean;
    team_email: boolean;
    product_updates_email: boolean;
    blog_posts_email: boolean;
    newsletters_email: boolean;
    
    // In-app preferences
    usage_alerts_in_app: boolean;
    team_in_app: boolean;
    product_updates_in_app: boolean;
    blog_posts_in_app: boolean;
    newsletters_in_app: boolean;
  }

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Company details state
  const [companyDetails, setCompanyDetails] = useState<CompanyFormData>({
    companyName: '',
    companyWebsite: '',
    companyIndustry: '',
    companySize: '',
    address: '',
    postalCode: '',
    city: '',
    country: '',
    additionalInfo: '',
  });
  const [isCompanySubmitting, setIsCompanySubmitting] = useState(false);

  // Notification preferences state
      const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferencesFormData>({
      // Email preferences
      usage_alerts_email: true,
      team_email: false,
      product_updates_email: false,
      blog_posts_email: false,
      newsletters_email: false,
      
      // In-app preferences
      usage_alerts_in_app: true,
      team_in_app: false,
      product_updates_in_app: false,
      blog_posts_in_app: false,
      newsletters_in_app: false,
    });
  const [isNotificationSubmitting, setIsNotificationSubmitting] = useState(false);
  const [isEmailChangeSubmitting, setIsEmailChangeSubmitting] = useState(false);
  const [emailChangeStatus, setEmailChangeStatus] = useState<string | null>(null);
  const [emailChangePolling, setEmailChangePolling] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first warning, 2 = final confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    
    if (tabParam && tabs.some(tab => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/settings?tab=${tabId}`, { replace: true });
  };

  // Load user profile, company data, and notification preferences when component mounts
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Load complete user profile
        try {
          const profile = await apiService.getCurrentUser();
          console.log('Loaded user profile:', profile);
          setUserProfile(profile);
        } catch (error) {
          console.error('Failed to load user profile:', error);
          // Set a fallback profile with current user data from context
          if (user) {
            setUserProfile({
              full_name: user.name || '',
              name: user.name || '',
              email: user.email || '',
              job_title: '',
              phone: '',
              timezone: 'UTC'
            });
          }
        }
        
        // Load company data
        try {
          const company = await apiService.getUserCompany();
          if (company) {
            setCompanyDetails({
              companyName: company.company_name || '',
              companyWebsite: company.company_website || '',
              companyIndustry: company.company_industry || '',
              companySize: company.company_size || '',
              address: company.address || '',
              postalCode: company.postal_code || '',
              city: company.city || '',
              country: company.country || '',
              additionalInfo: company.additional_info || '',
            });
          }
        } catch (error) {
          console.error('Failed to load company data:', error);
          // Keep default empty values if loading fails
        }
        
        // Load notification preferences
        try {
          console.log('🔍 Loading notification preferences...');
          const preferences = await apiService.getNotificationSettings();
          console.log('🔍 Loaded notification preferences:', preferences);
          console.log('🔍 Preferences type:', typeof preferences);
          console.log('🔍 Preferences keys:', preferences ? Object.keys(preferences) : 'null');
          if (preferences && preferences.usage_alerts_email !== undefined) {
            setNotificationPreferences({
              // Email preferences
              usage_alerts_email: preferences.usage_alerts_email ?? true,
              team_email: preferences.team_email ?? false,
              product_updates_email: preferences.product_updates_email ?? false,
              blog_posts_email: preferences.blog_posts_email ?? false,
              newsletters_email: preferences.newsletters_email ?? false,
              
              // In-app preferences
              usage_alerts_in_app: preferences.usage_alerts_in_app ?? true,
              team_in_app: preferences.team_in_app ?? false,
              product_updates_in_app: preferences.product_updates_in_app ?? false,
              blog_posts_in_app: preferences.blog_posts_in_app ?? false,
              newsletters_in_app: preferences.newsletters_in_app ?? false,
            });
          } else {
            console.log('No preferences found, creating default preferences');
            // Create default preferences if none exist
            const defaultPreferences = {
              usage_alerts_email: true,
              team_email: false,
              product_updates_email: false,
              blog_posts_email: false,
              newsletters_email: false,
              usage_alerts_in_app: true,
              team_in_app: false,
              product_updates_in_app: false,
              blog_posts_in_app: false,
              newsletters_in_app: false,
            };
            
            try {
              await apiService.createNotificationSettings(defaultPreferences);
              console.log('Created default notification preferences');
            } catch (createError) {
              console.error('Failed to create notification preferences:', createError);
            }
            
            setNotificationPreferences(defaultPreferences);
          }
        } catch (error) {
          console.error('Failed to load notification preferences:', error);
          // Set default preferences if API call fails
          const defaultPreferences = {
            usage_alerts_email: true,
            team_email: false,
            product_updates_email: false,
            blog_posts_email: false,
            newsletters_email: false,
            usage_alerts_in_app: true,
            team_in_app: false,
            product_updates_in_app: false,
            blog_posts_in_app: false,
            newsletters_in_app: false,
          };
          setNotificationPreferences(defaultPreferences);
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        // Keep default values if loading fails
      } finally {
        setIsLoadingProfile(false);
        setIsLoadingCompany(false);
      }
    };

    loadUserData();
  }, []);

  // Form setup first
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: '',
      jobTitle: '',
      phone: '',
      timezone: 'UTC',
    },
  });

  // Reset form when profile data is loaded
  useEffect(() => {
    if (userProfile) {
      console.log('Resetting profile form with data:', userProfile);
      resetProfile({
        name: userProfile.full_name || userProfile.name || '',
        jobTitle: userProfile.job_title || '',
        phone: userProfile.phone || '',
        timezone: userProfile.timezone || 'UTC',
      });
    }
  }, [userProfile, resetProfile]);

  // Fallback: Reset form with user context data if profile data is not available
  useEffect(() => {
    if (!userProfile && user) {
      console.log('Using fallback user data from context:', user);
      resetProfile({
        name: user.name || '',
        jobTitle: '',
        phone: '',
        timezone: 'UTC',
      });
    }
  }, [user, userProfile, resetProfile]);



  const timezoneOptions = [
    { value: "UTC", label: "UTC (Coordinated Universal Time)" },
    { value: "America/New_York", label: "Eastern Time (New York, Miami, Toronto)" },
    { value: "America/Chicago", label: "Central Time (Chicago, Houston, Mexico City)" },
    { value: "America/Denver", label: "Mountain Time (Denver, Phoenix, Calgary)" },
    { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles, San Francisco, Vancouver)" },
    { value: "America/Anchorage", label: "Alaska Time (Anchorage, Juneau)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (Honolulu, Hilo)" },
    { value: "Europe/London", label: "GMT/BST (London, Dublin, Lisbon)" },
    { value: "Europe/Paris", label: "Central European Time (Paris, Berlin, Rome)" },
    { value: "Europe/Athens", label: "Eastern European Time (Athens, Helsinki, Kiev)" },
    { value: "Europe/Moscow", label: "Moscow Time (Moscow, St. Petersburg)" },
    { value: "Asia/Dubai", label: "Gulf Standard Time (Dubai, Abu Dhabi, Muscat)" },
    { value: "Asia/Kolkata", label: "India Standard Time (Mumbai, Delhi, Bangalore)" },
    { value: "Asia/Shanghai", label: "China Standard Time (Shanghai, Beijing, Hong Kong)" },
    { value: "Asia/Tokyo", label: "Japan Standard Time (Tokyo, Osaka, Seoul)" },
    { value: "Asia/Singapore", label: "Singapore Time (Singapore, Kuala Lumpur, Manila)" },
    { value: "Australia/Sydney", label: "Australian Eastern Time (Sydney, Melbourne, Brisbane)" },
    { value: "Australia/Perth", label: "Australian Western Time (Perth, Darwin)" },
    { value: "Pacific/Auckland", label: "New Zealand Time (Auckland, Wellington)" },
  ];

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
  } = useForm<PasswordFormData>();

  const {
    register: registerEmailChange,
    handleSubmit: handleEmailChangeSubmit,
    formState: { errors: emailChangeErrors },
    reset: resetEmailChangeForm,
  } = useForm<EmailChangeFormData>();

  const {
    register: registerCompany,
    handleSubmit: handleCompanySubmit,
    formState: { errors: companyErrors },
    reset: resetCompanyForm,
  } = useForm<CompanyFormData>({
    defaultValues: companyDetails,
  });

  // Reset company form when company data is loaded
  useEffect(() => {
    if (companyDetails.companyName || companyDetails.companyWebsite) {
      resetCompanyForm(companyDetails);
    }
  }, [companyDetails, resetCompanyForm]);

  const tabs = [
    { id: 'account', name: 'Account', icon: UserIcon },
    { id: 'teams', name: 'Teams', icon: UsersIcon },
    { id: 'company-details', name: 'Company Details', icon: BuildingOfficeIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
  ];



  const onProfileSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      // Call the backend API to update profile
      await apiService.updateUserProfile({
        full_name: data.name,
        phone: data.phone,
        timezone: data.timezone,
        job_title: data.jobTitle,
      });

      toast.success('Profile updated successfully!');
      
      // Reload the profile data to reflect changes
      try {
        const updatedProfile = await apiService.getCurrentUser();
        setUserProfile(updatedProfile);
      } catch (error) {
        console.error('Failed to reload profile after update:', error);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Call the backend API to change password
      await apiService.changePassword({
        current_password: data.currentPassword,
        new_password: data.newPassword,
        confirm_password: data.confirmPassword,
      });
      
      toast.success('Password updated successfully!');
      resetPasswordForm();
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(`Failed to update password: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onEmailChangeSubmit = async (data: EmailChangeFormData) => {
    if (data.newEmail !== data.confirmEmail) {
      toast.error('Email addresses do not match.');
      return;
    }

    setIsEmailChangeSubmitting(true);
    try {
      const result = await authService.changeEmail(data.newEmail);
      toast.success(result.message);
      setEmailChangeStatus(result.status);
      resetEmailChangeForm();
      
      // Start polling for email change completion
      startEmailChangePolling();
      
    } catch (error) {
      console.error('Email change error:', error);
      toast.error(`Failed to change email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsEmailChangeSubmitting(false);
    }
  };

  // Polling mechanism to check email change status
  const startEmailChangePolling = () => {
    setEmailChangePolling(true);
    
    // Auto-clear after 10 minutes if still pending
    const autoTimeout = setTimeout(() => {
      setEmailChangeStatus(null);
      setEmailChangePolling(false);
      toast('Email change still in progress. Please check your email and refresh if needed.');
    }, 10 * 60 * 1000); // 10 minutes

    // Poll every 30 seconds with error handling
    const pollInterval = setInterval(async () => {
      try {
        // Try to complete email change (this will succeed if Supabase completed it)
        const result = await authService.completeEmailChange();
        // If this succeeds, email change is complete
        toast.success('Email change completed successfully!');
        setEmailChangeStatus(null);
        setEmailChangePolling(false);
        clearTimeout(autoTimeout);
        clearInterval(pollInterval);
        
        // Refresh the page to show new email
        window.location.reload();
        
      } catch (error) {
        // If it's a server error, stop polling to prevent spam
        if (error instanceof Error && error.message.includes('500')) {
          console.log('Server error, stopping polling');
          clearInterval(pollInterval);
          setEmailChangePolling(false);
          toast.error('Please refresh the page and try again');
          return;
        }
        // For other errors, continue polling
        console.log('Email change still pending...');
      }
    }, 30000); // 30 seconds

    // Clean up on component unmount
    return () => {
      clearTimeout(autoTimeout);
      clearInterval(pollInterval);
    };
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeleteStep(1);
    setDeleteConfirmation('');
  };

  const handleDeleteStep1 = () => {
    setDeleteStep(2);
  };

  const handleDeleteStep2 = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type "DELETE" to confirm account deletion.');
      return;
    }

    setIsDeleting(true);
    try {
      await apiService.deleteUserAccount();
      toast.success('Account deletion initiated. You will receive an email confirmation.');
      setShowDeleteModal(false);
      setDeleteStep(1);
      setDeleteConfirmation('');
      
      // Redirect to home page after a delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Account deletion error:', error);
      toast.error(`Failed to delete account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeleteConfirmation('');
  };

  // Company details functions
  const onCompanySubmit = async (data: CompanyFormData) => {
    setIsCompanySubmitting(true);
    try {
      // Map frontend field names to backend field names
      const companyPayload = {
        company_name: data.companyName,
        company_website: data.companyWebsite,
        company_industry: data.companyIndustry,
        company_size: data.companySize,
        address: data.address,
        postal_code: data.postalCode,
        city: data.city,
        country: data.country,
        additional_info: data.additionalInfo,
      };

      await apiService.updateUserCompany(companyPayload);
      
      // Update local state
      setCompanyDetails(data);
      
      // Reload company data to get the latest from backend
      try {
        const updatedCompany = await apiService.getUserCompany();
        if (updatedCompany) {
          setCompanyDetails({
            companyName: updatedCompany.company_name || '',
            companyWebsite: updatedCompany.company_website || '',
            companyIndustry: updatedCompany.company_industry || '',
            companySize: updatedCompany.company_size || '',
            address: updatedCompany.address || '',
            postalCode: updatedCompany.postal_code || '',
            city: updatedCompany.city || '',
            country: updatedCompany.country || '',
            additionalInfo: updatedCompany.additional_info || '',
          });
        }
      } catch (error) {
        console.error('Failed to reload company after update:', error);
      }
      
      toast.success('Company details updated successfully!');
    } catch (error) {
      console.error('Company details update error:', error);
      toast.error(`Failed to update company details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCompanySubmitting(false);
    }
  };

  const onNotificationPreferencesSubmit = async (data: NotificationPreferencesFormData) => {
    setIsNotificationSubmitting(true);
    try {
      // Convert time format from HH:MM to HH:MM:SS for backend
      const preferencesData = {
        ...data,
        // Backend expects email_types directly
      };

      await apiService.updateNotificationSettings(preferencesData);
      setNotificationPreferences(data);
      toast.success('Notification preferences updated successfully!');
    } catch (error) {
      console.error('Notification preferences update error:', error);
      toast.error(`Failed to update notification preferences: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsNotificationSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-8">
            {/* Profile Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
              {isLoadingProfile ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...registerProfile('name', { required: 'Name is required' })}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        profileErrors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {profileErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      readOnly
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      To change your email address, use the "Change Email" section below.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
                      Job Title
                    </label>
                    <input
                      type="text"
                      id="jobTitle"
                      {...registerProfile('jobTitle')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...registerProfile('phone')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                      Timezone
                    </label>
                    <select
                      id="timezone"
                      {...registerProfile('timezone')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    >
                      {timezoneOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
              )}
            </div>

            {/* Change Email */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Email Address</h3>
              <p className="text-sm text-gray-600 mb-4">
                Changing your email will require verification. You'll receive a confirmation email at your new address.
              </p>
              
              {emailChangeStatus && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-800">Email change in progress</h4>
                      <div className="mt-2 text-sm text-blue-700">
                        <p className="mb-2">We've sent verification emails to both addresses:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li><strong>Verification email</strong> → Your new email address</li>
                          <li><strong>Security notification</strong> → Your current email address</li>
                        </ol>
                        <p className="mt-2 text-xs">
                          Your email will update automatically once verification is complete. This may take a few minutes.
                        </p>
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                setIsEmailChangeSubmitting(true);
                                const result = await authService.completeEmailChange();
                                toast.success(result.message);
                                setEmailChangeStatus(null);
                                setEmailChangePolling(false);
                                window.location.reload();
                              } catch (error) {
                                if (error instanceof Error && error.message.includes('No pending email change')) {
                                  setEmailChangeStatus(null);
                                  setEmailChangePolling(false);
                                  toast('Email change process completed');
                                } else {
                                  toast.error('Email change still in progress. Please wait...');
                                }
                              } finally {
                                setIsEmailChangeSubmitting(false);
                              }
                            }}
                            disabled={isEmailChangeSubmitting}
                            className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                          >
                            {isEmailChangeSubmitting ? 'Checking...' : 'Already verified? Click here'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleEmailChangeSubmit(onEmailChangeSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700">
                      New Email Address
                    </label>
                    <input
                      type="email"
                      id="newEmail"
                      {...registerEmailChange('newEmail', {
                        required: 'New email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        emailChangeErrors.newEmail ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {emailChangeErrors.newEmail && (
                      <p className="mt-1 text-sm text-red-600">{emailChangeErrors.newEmail.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700">
                      Confirm New Email
                    </label>
                    <input
                      type="email"
                      id="confirmEmail"
                      {...registerEmailChange('confirmEmail', {
                        required: 'Please confirm your new email',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        emailChangeErrors.confirmEmail ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {emailChangeErrors.confirmEmail && (
                      <p className="mt-1 text-sm text-red-600">{emailChangeErrors.confirmEmail.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isEmailChangeSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {isEmailChangeSubmitting ? 'Changing Email...' : 'Change Email'}
                  </button>
                </div>
              </form>
            </div>

            {/* Change Password */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="currentPassword"
                      {...registerPassword('currentPassword', { required: 'Current password is required' })}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showCurrentPassword ? (
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <div className="relative mt-1">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        {...registerPassword('newPassword', { 
                          required: 'New password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' }
                        })}
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                          passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showNewPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <div className="relative mt-1">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        {...registerPassword('confirmPassword', { required: 'Please confirm your new password' })}
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                          passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>

            {/* Danger Zone */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-red-900">Delete Account</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Once you delete your account, there is no going back. Please be certain.
                    </p>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'teams':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Management</h3>
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-8">
                <div className="text-center">
                  <UsersIcon className="h-16 w-16 text-purple-600 mx-auto mb-6" />
                  <h4 className="text-2xl font-semibold text-gray-900 mb-4">Team Collaboration</h4>
                  <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                    Collaborate with colleagues by creating teams and managing shared access to TrendBeat features. 
                    Perfect for agencies, brands, and growing businesses.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <UsersIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <h5 className="font-semibold text-gray-900 mb-2">Create Teams</h5>
                      <p className="text-sm text-gray-600">Set up teams with custom roles and permissions</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h5 className="font-semibold text-gray-900 mb-2">Share Access</h5>
                      <p className="text-sm text-gray-600">Control who can access which features and data</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <BellIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <h5 className="font-semibold text-gray-900 mb-2">Team Alerts</h5>
                      <p className="text-sm text-gray-600">Get notified about team activity and updates</p>
                    </div>
                  </div>
                  
                  {/* Preview of what teams would look like */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                    <h5 className="font-semibold text-gray-900 mb-4">Preview: Your Teams</h5>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600">M</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Marketing Team</p>
                            <p className="text-sm text-gray-500">5 members • Admin</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">A</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Analytics Team</p>
                            <p className="text-sm text-gray-500">3 members • Member</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg">
                        <button className="flex items-center space-x-2 text-gray-500 hover:text-gray-700">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-sm font-medium">Create New Team</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Coming Soon:</strong> Team management features will be available for Professional and Business plans.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      toast('Team management features coming soon! Stay tuned for updates.', {
                        icon: 'ℹ️',
                        duration: 4000,
                      });
                    }}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
                  >
                    Get Notified When Available
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'company-details':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Company Details</h3>
              <p className="text-gray-600 mb-6">
                Add your company information for billing purposes, VAT calculations, and invoice generation.
              </p>
              
              {isLoadingCompany ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleCompanySubmit(onCompanySubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      {...registerCompany('companyName')}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        companyErrors.companyName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      defaultValue={companyDetails.companyName}
                    />
                    {companyErrors.companyName && (
                      <p className="mt-1 text-sm text-red-600">{companyErrors.companyName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-700">
                      Website
                    </label>
                    <input
                      type="url"
                      id="companyWebsite"
                      {...registerCompany('companyWebsite')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="companyIndustry" className="block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <select
                      id="companyIndustry"
                      {...registerCompany('companyIndustry')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    >
                      <option value="">Select Industry</option>
                      <option value="technology">Technology</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="marketing">Marketing & Advertising</option>
                      <option value="fashion">Fashion & Beauty</option>
                      <option value="lifestyle">Lifestyle & Wellness</option>
                      <option value="media">Media & Entertainment</option>
                      <option value="consulting">Consulting</option>
                      <option value="education">Education</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="companySize" className="block text-sm font-medium text-gray-700">
                      Company Size
                    </label>
                    <select
                      id="companySize"
                      {...registerCompany('companySize')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    >
                      <option value="">Select Size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501+">501+ employees</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      {...registerCompany('address')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      {...registerCompany('postalCode')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      {...registerCompany('city')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                      Country
                    </label>
                    <select
                      id="country"
                      {...registerCompany('country')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    >
                      <option value="">Select Country</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="Sweden">Sweden</option>
                      <option value="Norway">Norway</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Spain">Spain</option>
                      <option value="Italy">Italy</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Austria">Austria</option>
                      <option value="Finland">Finland</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Singapore">Singapore</option>
                      <option value="Japan">Japan</option>
                      <option value="South Korea">South Korea</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">
                    Additional Information (Optional)
                  </label>
                  <textarea
                    id="additionalInfo"
                    {...registerCompany('additionalInfo')}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Include VAT number, company registration number, or other billing details</p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isCompanySubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {isCompanySubmitting ? 'Saving...' : 'Save Company Details'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="currentPassword"
                      {...registerPassword('currentPassword', { required: 'Current password is required' })}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      {...registerPassword('newPassword', {
                        required: 'New password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      {...registerPassword('confirmPassword', { required: 'Please confirm your password' })}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                        passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Two-Factor Authentication</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">Add an extra layer of security to your account</p>
                  <p className="text-xs text-gray-500 mt-1">Currently disabled</p>
                </div>
                <button 
                  onClick={() => {/* TODO: Implement 2FA enable */}}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Enable 2FA
                </button>
              </div>
              
              {/* 2FA Setup Modal would go here */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">How 2FA Works</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                  <li>• Scan the QR code or enter the secret key</li>
                  <li>• Enter the 6-digit code from your app to verify</li>
                  <li>• Save your backup codes in a secure location</li>
                </ul>
              </div>
            </div>
          </div>
        );



            case 'notifications':
        return (
          <div className="space-y-6">
            <form onSubmit={(e) => {
              e.preventDefault();
              onNotificationPreferencesSubmit(notificationPreferences);
            }} className="space-y-8">
              
              {/* Events Section */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-0">Events</h4>
                <div className="border-b border-gray-200 pb-1 mb-2"></div>
                <p className="text-sm text-gray-600">Control the notifications related to your usage of TrendBeat.</p>
                
                <div className="mt-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Notification Type</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-900">Email</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-900">In-App</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Usage Alerts</p>
                            <p className="text-xs text-gray-500">API limits, quota warnings, usage thresholds</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.usage_alerts_email}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              usage_alerts_email: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.usage_alerts_in_app}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              usage_alerts_in_app: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Team</p>
                            <p className="text-xs text-gray-500">New members, role changes, team activity</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.team_email}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              team_email: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.team_in_app}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              team_in_app: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* TrendBeat Updates Section */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-0">TrendBeat Updates</h4>
                <div className="border-b border-gray-200 pb-1 mb-2"></div>
                <p className="text-sm text-gray-600">Get notified when we release new features or educational content.</p>
                
                <div className="mt-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Notification Type</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-900">Email</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-900">In-App</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Product Updates</p>
                            <p className="text-xs text-gray-500">New features, improvements, announcements</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.product_updates_email}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              product_updates_email: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.product_updates_in_app}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              product_updates_in_app: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Blog Posts</p>
                            <p className="text-xs text-gray-500">Guides, tutorials, best practices</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.blog_posts_email}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              blog_posts_email: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.blog_posts_in_app}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              blog_posts_in_app: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Newsletters</p>
                            <p className="text-xs text-gray-500">Industry analyses and trend insights</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.newsletters_email}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              newsletters_email: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.newsletters_in_app}
                            onChange={(e) => setNotificationPreferences(prev => ({
                              ...prev,
                              newsletters_in_app: e.target.checked
                            }))}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setNotificationPreferences(prev => ({
                      ...prev,
                      // Email preferences
                      usage_alerts_email: false,
                      team_email: false,
                      product_updates_email: false,
                      blog_posts_email: false,
                      newsletters_email: false,
                      
                      // In-app preferences
                      usage_alerts_in_app: false,
                      team_in_app: false,
                      product_updates_in_app: false,
                      blog_posts_in_app: false,
                      newsletters_in_app: false,
                    }));
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Turn off all notifications
                </button>
                
                <button 
                  type="submit"
                  disabled={isNotificationSubmitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isNotificationSubmitting ? 'Saving...' : 'Save settings'}
                </button>
              </div>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto px-6 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.length > 8 ? tab.name.substring(0, 8) + '...' : tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100vh',
            width: '100vw',
            margin: 0,
            padding: '1rem',
            zIndex: 9999
          }}
        >
          <div className="relative w-full max-w-md mx-auto bg-white rounded-lg shadow-xl">
            <div className="p-6">
              {deleteStep === 1 ? (
                <>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-6">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">
                    Delete Account
                  </h3>
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 text-center mb-6">
                      Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">What happens when you delete your account:</h4>
                          <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                            <li>All your data will be permanently deleted</li>
                            <li>Your subscription will be cancelled</li>
                            <li>You will lose access to all TrendBeat features</li>
                            <li>This action cannot be undone</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleCancelDelete}
                      className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteStep1}
                      className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Yes, Delete My Account
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-6">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">
                    Final Confirmation
                  </h3>
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 text-center mb-6">
                      This is your final chance to cancel. Type <strong>DELETE</strong> in the box below to confirm account deletion.
                    </p>
                    <div className="mb-6">
                      <label htmlFor="deleteConfirmation" className="block text-sm font-medium text-gray-700 mb-2">
                        Type "DELETE" to confirm:
                      </label>
                      <input
                        type="text"
                        id="deleteConfirmation"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        placeholder="Type DELETE here"
                      />
                    </div>
                  </div>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleCancelDelete}
                      className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteStep2}
                      disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                      className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isDeleting ? 'Deleting...' : 'Permanently Delete Account'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 